'use client';
import ShopLocalButton from '@components/Product/CallToActionButton/_ShopLocalButton';
import { isSSR } from '@repo/ui/src/utils/isSSR';
import { MouseEvent, useEffect, useState } from 'react';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { CTAButtonProps } from '@repo/ui/src/types';
import { useVipAccess } from '@hooks/customer';
import { useDebounceCallback } from '@hooks/useDebouncedCallback';
import Product from '@models/product';
import AddToCartButton from './_AddToCartButton';
import BuyOnlineButton from './_BuyOnlineButton';
import OutOfStockButton from './_OutOfStockButton';

const buttonMap = {
  addToCart: AddToCartButton,
  hideCTA: () => null,
  locally: BuyOnlineButton,
  shopLocal: ShopLocalButton,
  outOfStock: OutOfStockButton,
};

interface Props {
  buttonProps?: CTAButtonProps;
  product: Product;
  customProductPrice?: number;
  onClickAddToCart: (e: MouseEvent<HTMLElement>) => void;
  buttonProcessing?: boolean;
  processingText?: string;
  className?: string;
}

const getButtonType = (
  product: Product,
  hasVipAccess: boolean,
  eCommEnabled: boolean,
): keyof typeof buttonMap => {
  // hideCTA trumps all other options
  if (!product.purchasing || product.purchasing.hideCTA) {
    return 'hideCTA';
  }

  // if ecomm is disabled for the site always show shop local
  if (!eCommEnabled) {
    return 'shopLocal';
  }

  // if product is orderable (either not requiring VIP access or when the user has vip access, show addToCart
  if (
    product.isOrderable &&
    (!product.purchasing.vipOnly || (product.purchasing.vipOnly && hasVipAccess))
  ) {
    return 'addToCart';
  }

  // if product is not orderable, but is a traeger exclusive show out of stock
  if (product.purchasing.traegerExclusive) {
    return 'outOfStock';
  }

  // otherwise if product is not orderable show shop local
  return 'locally';
};

const CallToActionButton = ({ product, ...props }: Props) => {
  const { eCommEnabled } = useCurrentUrl();
  const { vip, isLoading } = useVipAccess();
  const [debouncedButtonType, setDebouncedButtonType] = useState<keyof typeof buttonMap | null>(
    null,
  );

  const buttonType =
    !isSSR && !(isLoading && product.purchasing?.vipOnly)
      ? getButtonType(product, vip ?? false, eCommEnabled)
      : null;

  // Create debounced callback to update button type
  const updateButtonType = useDebounceCallback(
    (type: keyof typeof buttonMap) => {
      setDebouncedButtonType(type);
    },
    [],
    300,
  );

  // Call debounced callback whenever buttonType changes
  useEffect(() => {
    if (buttonType === null) {
      return;
    }
    updateButtonType(buttonType);
  }, [buttonType, updateButtonType]);

  // Early returns after all hooks
  if (isSSR) return null;

  // For VIP-only products, wait until VIP status is loaded to prevent flash
  if (isLoading && product.purchasing?.vipOnly) return null;

  // Don't render until the button type has stabilized
  if (debouncedButtonType === null) return null;

  const ButtonComponent = buttonMap[debouncedButtonType];

  return <ButtonComponent product={product} {...props} />;
};

export default CallToActionButton;
